// ./src/utils/api/EventService.ts
//
// Event service — OpResult-native. Minimal for now: creating an event from the
// Create Event wizard. Read/list/update land when the events list page is built.
// Field names mirror the eventsmagic-api Event SDL (see app/graphql/types/eventTypes).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// Lean fields for list rows / cards (no companions).
const EVENT_LIST_FIELDS = `
  id
  title
  subtitle
  slug
  eventCategoryId
  startsAt
  endsAt
  seriesEndsAt
  nextDateStartsAt
  isAllDay
  timezone
  scheduleMode
  hasTimeSlots
  eventFormat
  venueId
  onlineMeetingUrl
  eventStatus
  publishedAt
  eventMode
  guestTracking
  isListed
  capacity
  status
  rights
  createdAt
  updatedAt
`;

const EVENT_LIST_QUERY = gql`
  query EventList($filter: EventFilter, $params: PaginationAndSorting) {
    eventList(filter: $filter, params: $params) {
      code
      data { ${EVENT_LIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// ROADMAP #112 — All Events PAGES now: one tab (`timeframe`) and the search are server filters, and `total` is the whole
// set's size (Load more + the tab counts). ⚠ Its own query, not EVENT_LIST_QUERY with `total` added: Home and the
// inventory event picker read the plain list, and a field the gateway has not composed yet would fail THEIR reads too.
const EVENT_PAGE_QUERY = gql`
  query EventPage($filter: EventFilter, $params: PaginationAndSorting) {
    eventList(filter: $filter, params: $params) {
      code
      total
      data { ${EVENT_LIST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// A fuller field set for a single event (create result + dashboard + editor).
const EVENT_RESULT_FIELDS = `
  id
  accountId
  title
  subtitle
  summary
  slug
  eventCategoryId
  eventNature
  audience
  hostedBy
  startsAt
  endsAt
  isAllDay
  timezone
  scheduleMode
  hasTimeSlots
  eventFormat
  venueId
  onlineMeetingUrl
  locationVisibility
  environment
  weatherSensitive
  organizerProfileId
  coverUploadedFileId
  coverUrl
  coverVideoUploadedFileId
  coverVideoUrl
  coverUploadedFile { id fileUrl thumbnailUrl }
  coverVideoUploadedFile { id fileUrl }
  eventStatus
  publishedAt
  isListed
  channel
  eventMode
  guestTracking
  rsvpButtonSet
  guestsNeedApproval
  approvalDepositAmount
  ageOfMajority
  doorsOpenMinutes
  capacity
  viableFillRate
  viableCostCoverage
  rsvpGroupSize
  maxTicketsPerOrder
  rsvpOpensAt
  rsvpDeadline
  rsvpDeadlineDaysBefore
  enableWaitlist
  currencyCode
  isTestMode
  serviceFeePayer
  feeMode
  taxSettingId
  minOrderAmount
  cancelReason
  settings
  themeId
  themeOverrides
  customCss
  status
  rights
  createdAt
  updatedAt
`;

const EVENT_CREATE_MUTATION = gql`
  mutation EventCreate($params: EventInput) {
    eventCreate(params: $params) {
      code
      data { ${EVENT_RESULT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_GET_QUERY = gql`
  query EventGet($id: ID) {
    eventGet(id: $id) {
      code
      data { ${EVENT_RESULT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// ROADMAP #60 — the in-session upgrade offer, read ON ITS OWN: an event read never depends on it (a gateway not yet
// recomposed with the field refuses the whole query — the event page must not go with it).
const EVENT_UPGRADE_OFFER_QUERY = gql`
  query EventUpgradeOffer($id: ID) {
    eventGet(id: $id) {
      code
      data { id upgradeOffer { percentOff endsAt secondsLeft } }
      errors { name errors warnings }
    }
  }
`;

const EVENT_UPDATE_MUTATION = gql`
  mutation EventUpdate($where: EventWhereInput, $params: EventInput) {
    eventUpdate(where: $where, params: $params) {
      code
      data { ${EVENT_RESULT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_REMOVE_MUTATION = gql`
  mutation EventRemove($where: EventWhereInput) {
    eventRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const EVENT_SLUG_AVAILABLE_QUERY = gql`
  query EventSlugAvailable($slug: String, $eventId: ID) {
    eventSlugAvailable(slug: $slug, eventId: $eventId) {
      code
      data { slug available reason }
      errors { name errors warnings }
    }
  }
`;

const EVENT_PLAN_USAGE_QUERY = gql`
  query EventPlanUsage($params: EventPlanUsageInput) {
    eventPlanUsage(params: $params) {
      code
      data { eventId featureCode used }
      errors { name errors warnings }
    }
  }
`;

// A2b #56 — what cancelling would do (and, afterwards, how the refunds are going). The two share
// one field list because the dialog renders the SAME numbers before and after: a host reopening it
// is asking the same question.
const EVENT_CANCEL_FIELDS = `
  eventId
  recipients
  unreachable
  attendees
  crew
  sponsors
  volunteers
  artists
  refundableOrders
  refundableOrderAmount
  refundableSponsorships
  refundableSponsorshipAmount
  refundableSignupOrders
  refundableSignupOrderAmount
  refundableTotal
  currencyCode
  refundsQueued
`;

/**
 * ROADMAP A2b #63 — which event features already have rows on this event, so the menu never tucks a page that has data
 * behind "Show more". `unknown` = could not be checked (file storage down): the menu treats those as having data.
 */
const EVENT_FEATURE_USAGE_QUERY = gql`
  query EventFeatureUsage($params: EventFeatureUsageInput) {
    eventFeatureUsage(params: $params) {
      code
      data {
        eventId
        withData
        unknown
      }
      errors { name errors warnings }
    }
  }
`;

export interface EventFeatureUsage {
  eventId: string;
  withData: string[];
  unknown: string[];
}

/**
 * ROADMAP #161 b — what this event sells online that costs money (`tickets` | `signup_items` | `sponsorships`), so
 * "Connect Stripe" is offered only where it is needed. No card READINESS: `useCardPayments` asks Stripe itself.
 * ⚠ A failed read is not `kinds: []` — the caller says nothing rather than "sells nothing".
 */
const EVENT_PAID_SALES_QUERY = gql`
  query EventPaidSales($params: EventPaidSalesInput) {
    eventPaidSales(params: $params) {
      code
      data {
        eventId
        kinds
        isTestMode
      }
      errors { name errors warnings }
    }
  }
`;

export interface EventPaidSales {
  eventId: string;
  /** `PAID_SALE_KIND` codes (components/features/EventSetup/setupStepRules.ts). */
  kinds: string[];
  isTestMode: boolean;
}

/**
 * ROADMAP #188 — the event's TEST records: while it is in test mode the current test run's (and how many of the run's
 * test places are used), once it is live the hidden ones. Needs the event-settings right (it lists guests' names).
 */
const EVENT_TEST_RECORDS_FIELDS = `
  code
  data {
    eventId
    isTestMode
    hidden
    limit
    placesUsed
    guestCount
    orderCount
    ticketCount
    signupOrderCount
    applicationCount
    paymentCount
    guests { id name guestRole rsvpStatus createdAt }
    orders { id referenceCode totalAmount currencyCode orderStatus quantity createdAt }
  }
  errors { name errors warnings }
`;

const EVENT_TEST_RECORDS_QUERY = gql`
  query EventTestRecords($params: EventTestRecordsInput) {
    eventTestRecords(params: $params) {
      ${EVENT_TEST_RECORDS_FIELDS}
    }
  }
`;

/** ROADMAP #188 — clears them (the current run's and the hidden ones); answers what is left. */
const EVENT_TEST_RECORDS_CLEAR_MUTATION = gql`
  mutation EventTestRecordsClear($params: EventTestRecordsInput) {
    eventTestRecordsClear(params: $params) {
      ${EVENT_TEST_RECORDS_FIELDS}
    }
  }
`;

/**
 * ROADMAP #190 — the cards the event dashboard draws (in order, each shown or hidden), resolved by the server: the event's
 * own list, else its ACCOUNT's default, else the built-in order. ⓘ Its own query, not a field of the event: a gateway not
 * yet recomposed refuses a whole query naming an unknown field, and that must cost the card order, never the event.
 */
const EVENT_DASHBOARD_CARDS_FIELDS = `
  code
  data {
    eventId
    cards { id hidden }
    source
    accountCards { id hidden }
    accountSource
  }
  errors { name errors warnings }
`;

const EVENT_DASHBOARD_CARDS_QUERY = gql`
  query EventDashboardCards($params: EventDashboardCardsInput) {
    eventDashboardCards(params: $params) {
      ${EVENT_DASHBOARD_CARDS_FIELDS}
    }
  }
`;

/** ROADMAP #190 — saves the event's own list; `cards: null` drops it (the account's default applies). */
const EVENT_DASHBOARD_CARDS_SAVE_MUTATION = gql`
  mutation EventDashboardCardsSave($params: EventDashboardCardsSaveInput) {
    eventDashboardCardsSave(params: $params) {
      ${EVENT_DASHBOARD_CARDS_FIELDS}
    }
  }
`;

/** ROADMAP #190 — one card: its id (`utils/dashboardCards`) and whether the host hid it. */
export interface EventDashboardCard {
  id: string;
  hidden: boolean;
}

export interface EventDashboardCards {
  eventId: string;
  /** Every card, in drawing order. */
  cards: EventDashboardCard[];
  /** event | account | default — where `cards` came from. */
  source: string;
  /** What the event comes back to without its own list: the account's default, else the built-in order. */
  accountCards: EventDashboardCard[];
  /** account | default. */
  accountSource: string;
}

export interface EventTestRecordGuest {
  id: string;
  name: string | null;
  guestRole: string | null;
  rsvpStatus: string | null;
  createdAt: string | null;
}

export interface EventTestRecordOrder {
  id: string;
  referenceCode: string | null;
  totalAmount: number | null;
  currencyCode: string | null;
  orderStatus: string | null;
  quantity: number | null;
  createdAt: string | null;
}

export interface EventTestRecords {
  eventId: string;
  isTestMode: boolean;
  /** True: the event is live and these are its HIDDEN test records. */
  hidden: boolean;
  limit: number;
  placesUsed: number;
  guestCount: number;
  orderCount: number;
  ticketCount: number;
  signupOrderCount: number;
  applicationCount: number;
  paymentCount: number;
  guests: EventTestRecordGuest[];
  orders: EventTestRecordOrder[];
}

/**
 * #62 P6(d4) — a one-hour link to the event's website as guests will see it. A DRAFT event's site is reachable ONLY
 * through one (the owner or crew mints it); a published event's link works the same, the preview is simply ignored.
 */
const EVENT_PREVIEW_LINK_MUTATION = gql`
  mutation EventPreviewLink($params: EventPreviewLinkInput) {
    eventPreviewLink(params: $params) {
      code
      data {
        eventId
        url
        expiresAt
      }
      errors { name errors warnings }
    }
  }
`;

export interface EventPreviewLink {
  eventId: string;
  /** Open in a new tab: it forwards to the event website with the preview remembered for that event. */
  url: string;
  expiresAt: string;
}

const EVENT_CANCEL_STATUS_QUERY = gql`
  query EventCancelStatus($params: EventCancelStatusInput) {
    eventCancelStatus(params: $params) {
      code
      data {
        ${EVENT_CANCEL_FIELDS}
        eventStatus
        canceledAt
        cancelReason
        canRefund
        refundsDone
        refundsFailed
        refundsSkipped
        refundedSoFar
      }
      errors { name errors warnings }
    }
  }
`;

const EVENT_CANCEL_RETRY_MUTATION = gql`
  mutation EventCancelRetryRefunds($params: EventCancelStatusInput) {
    eventCancelRetryRefunds(params: $params) {
      code
      data { eventId requeued }
      errors { name errors warnings }
    }
  }
`;

const EVENT_CANCEL_MUTATION = gql`
  mutation EventCancel($params: EventCancelInput) {
    eventCancel(params: $params) {
      code
      data {
        ${EVENT_CANCEL_FIELDS}
        canceledAt
        cancelReason
        notified
        notifyFailed
      }
      errors { name errors warnings }
    }
  }
`;

// ROADMAP #116 — copy an event (the API's docs/COPY-EVENT.md).
const EVENT_COPY_PLAN_QUERY = gql`
  query EventCopyPlan($params: EventCopyPlanInput) {
    eventCopyPlan(params: $params) {
      code
      data {
        eventId
        title
        startsAt
        timezone
        recurring
        filesNote
        groups { key label people defaultOn detail count planNote }
      }
      errors { name errors warnings }
    }
  }
`;

// ROADMAP #231 — what the event uses that its plan may not include (the check publishing and going live run).
const EVENT_PLAN_CHECK_QUERY = gql`
  query EventPlanCheck($params: EventPlanCheckInput) {
    eventPlanCheck(params: $params) {
      code
      data {
        eventId
        needsUpgrade
        uses { featureCode featureName features items total included planLimit }
      }
      errors { name errors warnings }
    }
  }
`;

const EVENT_COPY_MUTATION = gql`
  mutation EventCopy($params: EventCopyInput) {
    eventCopy(params: $params) {
      code
      data {
        event { id title slug }
        leftOut { group label reason }
      }
      errors { name errors warnings }
    }
  }
`;

/** ROADMAP #231 — one plan feature the event uses, and what its plan says about it (`eventPlanCheck`). */
export interface EventPlanUse {
  featureCode: string;
  featureName: string;
  /** The Event features switched on that need it ("Party games"). */
  features: string[];
  /** The event's records and settings that use it ("3 polls", "custom CSS"). */
  items: string[];
  /** A limit: the most the event has (questions per form). Null for an on/off feature. */
  total: number | null;
  /** The plan (or the event's own upgrade) includes it. Always true while paywalls are off. */
  included: boolean;
  /** A limit the plan refuses: what it allows. */
  planLimit: number | null;
}

/** ROADMAP #231 — what publishing / going live would ask the plan about, with the answers. */
export interface EventPlanCheck {
  eventId: string;
  /** Something is not included: publishing (going live) is refused until it is upgraded or taken off. */
  needsUpgrade: boolean;
  uses: EventPlanUse[];
}

/** One part of the event the copy can take (`eventCopyPlan`). */
export interface EventCopyGroup {
  key: string;
  label: string;
  people: boolean;
  defaultOn: boolean;
  /** "7 ticket types, 1 discount code". */
  detail: string;
  count: number;
  /** What the plan would leave out of it, in the plan's words. */
  planNote?: string | null;
}

export interface EventCopyPlan {
  eventId: string;
  /** "<title> (copy)". */
  title: string;
  startsAt?: string | null;
  timezone?: string | null;
  recurring: boolean;
  filesNote: string;
  groups: EventCopyGroup[];
}

/** Something the host ticked that the copy left out, and why. */
export interface EventCopyLeftOut {
  group: string;
  label: string;
  reason: string;
}

export interface EventCopyReport {
  event: { id: string; title?: string | null; slug?: string | null };
  leftOut: EventCopyLeftOut[];
}

// =============================================================================
// Types (only the fields the wizard sends / reads)
// =============================================================================

// Recurrence rule stored in event.settings.recurrence (mirrors the API boundary).
export const RECURRENCE_FREQ = {
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  YEARLY: "yearly",
} as const;

export type RecurrenceFreq = (typeof RECURRENCE_FREQ)[keyof typeof RECURRENCE_FREQ];

export interface RecurrenceRule {
  freq: RecurrenceFreq;
  interval: number;
  byWeekday?: number[]; // 0=Sun..6=Sat
  byMonthday?: number[]; // 1..31 (monthly)
  byMonth?: number[]; // 1..12 (legacy yearly)
  byYearDay?: { month: number; day: number }[]; // explicit yearly (month,day) pairs
  startTime?: string | null; // "HH:mm"
  endTime?: string | null;
  until?: string | null; // "YYYY-MM-DD"
  count?: number | null;
  rsvpEnabled?: boolean | null; // generated occurrences accept RSVPs (default true)
}

/** Who absorbs our platform service fee on a ticketed event. */
export const SERVICE_FEE_PAYER = {
  OWNER: "owner", // organizer absorbs the fee (guest pays face value)
  GUEST: "guest", // fee added on top of the guest's total
} as const;

export type ServiceFeePayer = (typeof SERVICE_FEE_PAYER)[keyof typeof SERVICE_FEE_PAYER];

/**
 * How ticket fees are passed to the guest (mirrors the API boundary FEE_MODE, R14). Supersedes
 * SERVICE_FEE_PAYER:
 *   - absorb            — host covers all fees (guest pays ticket face + tax).
 *   - service           — guest also pays our service fee.
 *   - service_processor — guest also pays our service fee AND the estimated card fee.
 */
export const FEE_MODE = {
  ABSORB: "absorb",
  SERVICE: "service",
  SERVICE_PROCESSOR: "service_processor",
} as const;

export type FeeMode = (typeof FEE_MODE)[keyof typeof FEE_MODE];

/** Which RSVP buttons the guest sees (mirrors the API boundary RSVP_BUTTON_SET). */
export const RSVP_BUTTON_SET = {
  YES_MAYBE_NO: "yes_maybe_no", // three response choices
  YES_NO: "yes_no", // attending or not
  SUBMIT: "submit", // a single register/submit button (no yes/no)
} as const;

export type RsvpButtonSet = (typeof RSVP_BUTTON_SET)[keyof typeof RSVP_BUTTON_SET];

/** The event's physical setting (mirrors the API boundary EVENT_ENVIRONMENT). null = unspecified. */
export const EVENT_ENVIRONMENT = {
  INDOOR: "indoor",
  OUTDOOR: "outdoor",
  MIXED: "mixed", // partly indoor, partly outdoor
} as const;

export type EventEnvironment = (typeof EVENT_ENVIRONMENT)[keyof typeof EVENT_ENVIRONMENT];

/** Opaque/display selling settings kept in event.settings.payments (not columns). */
export interface EventPaymentSettings {
  statementDescriptor?: string | null; // appears on the guest's bank statement
  glCode?: string | null; // accounting-integration code (opaque to us)
  ticketLabel?: string | null; // generic noun for tickets (e.g. "Passes", "Weeks")
  addonLabel?: string | null; // generic noun for add-ons (e.g. "Extras")
  // Whether a buyer may transfer/give away a ticket to someone else. DEFAULTS TO ON (a missing value =
  // allowed); set false to forbid transfers (e.g. events with a screening/vetting process).
  allowTicketTransfer?: boolean | null;
  // A2e #87 — checkout asks (and requires) the buyer's postal address. DEFAULTS TO ON (a missing value = asked).
  askBuyerAddress?: boolean | null;
}

/** The event.settings jsonb blob (long-tail toggles); recurrence lives here. */
/**
 * One RSVP coupon grant rule. `grantPer: 'guest'` is per HEAD — the quantity is
 * multiplied by the RSVP's party size; `'group'` is one lot per RSVP group (an
 * ungrouped guest counts as their own party of one).
 */
export interface CouponGrantRule {
  couponTypeId: string;
  quantity: number;
  grantPer: "guest" | "group";
}

export interface EventCouponSettings {
  grantRules?: CouponGrantRule[] | null;
  /**
   * Auto-issue the grant rules when a guest RSVPs (or is approved) — no manual "Generate from rules"
   * needed. DEFAULTS TO ON (a missing value is treated as true, matching the backend); set false to keep
   * issuing manually only.
   */
  autoGrant?: boolean | null;
  /**
   * A4b — the date this event's vouchers stop being worth anything. Per EVENT, not per voucher type: a
   * voucher type is account-wide and may be used by several events, so an expiry on the type would
   * expire it everywhere it is used. Null / missing = they never expire.
   * ⚠ ENFORCED at redemption by `CouponCore.redeem`, not just printed — see ROADMAP A4b.
   */
  expiresAt?: string | null;
  /**
   * Where this event's vouchers are redeemed unless a voucher type names its own place — a service point of the event or
   * its venue (soft ref; read through `couponPlaceSettingsOf`, `@/utils/couponPlaces`). Null = not said.
   */
  redeemServicePointId?: string | null;
  /** A voucher type redeemed at its own place ("Drink token → Bar"). One line per type. */
  redeemAt?: { couponTypeId: string; servicePointId: string }[] | null;
  /** Free text printed across the top / bottom of every voucher of this event. */
  printHeader?: string | null;
  printFooter?: string | null;
}

export interface EventBibSettings {
  /** manual | on_registration — when bib numbers are assigned (sport events). */
  assignmentMode?: string | null;
}

/**
 * How the cover hero is presented on the event website (event.settings.hero).
 * Consumed by events-web via the public read's `hero` field.
 */
export interface EventHeroSettings {
  /** Size the cover to (nearly) the full viewport height (minus the header). */
  fullScreen?: boolean | null;
  /** Show the event identity text (title / subtitle / date-time / location) on the hero. Default true. */
  showDetails?: boolean | null;
  /** Cover video: start playing automatically (unmuted autoplay may be blocked by the browser). */
  videoAutoplay?: boolean | null;
  /** Cover video: start muted. Default true. */
  videoMuted?: boolean | null;
  /** Cover video: loop playback. */
  videoLoop?: boolean | null;
  /** B18 — how the hero arrives on the website: "none" | "rise" | "highlight" | "spray". */
  entrance?: string | null;
  /** B19 — the main RSVP / Buy button nudges now and then. */
  ctaNudge?: boolean | null;
  /** B18 — the page's animated background: "theme" (follow the theme) | "none" | an effect (utils/heroMotion.ts). */
  pageEffect?: string | null;
  /** B18 round 2 — how the cover picture arrives: "auto" (match the title) | "none" | a preset (utils/heroMotion.ts). */
  coverEntrance?: string | null;
  /** B18 round 3 — the opening scene the guest taps open: "none" | a scene (utils/heroMotion.ts). */
  opener?: string | null;
}

export interface EventSettings {
  /** #289 — a gift during registration: on, the RSVP form and the ticket purchase offer these of the event's causes. */
  registrationGift?: { enabled?: boolean; causeIds?: string[] } | null;
  /**
   * ROADMAP #62 / #63 — the event features in use. ⚠ ABSENT = everything on (every event made before this existed);
   * an empty list = nothing optional on. Codes: config/eventSetup.generated EVENT_FEATURE_CODES.
   */
  features?: { on: string[] } | null;
  recurrence?: RecurrenceRule | null;
  payments?: EventPaymentSettings | null;
  coupons?: EventCouponSettings | null;
  bib?: EventBibSettings | null;
  hero?: EventHeroSettings | null;
  /** Custom label for the primary register/RSVP button on the website. Blank = default. */
  rsvpButtonLabel?: string | null;
  /** Days before the RSVP/ticket deadline to start showing the "closing soon" reminder (default 7). */
  rsvpReminderDays?: number | null;
  /** Per-event display order of category GROUPS, keyed by category group → ordered ids. */
  categoryOrder?: Record<string, string[]> | null;
  /** Website RSVP settings (guest-group selection + signup-list opt-in offered on the form). */
  rsvp?: {
    groups?: {
      selectableSetIds?: string[];
      creatableSetIds?: string[];
      allowPassword?: boolean;
    } | null;
    signups?: {
      enabled?: boolean;
    } | null;
    /** Members-only RSVP (owner, 2026-10-13): `required` = guests enter a membership number; its types (none = any). */
    membership?: { mode?: 'any' | 'required' | string; membershipTypeIds?: string[] } | null;
  } | null;
  /** Check-in settings — e.g. show each guest a QR of their check-in code on the confirmation + email. */
  checkIn?: {
    guestQr?: boolean;
    /** #228 — false = a guest who checked out cannot come back in (absent = allowed). A ticket type may override it. */
    reentry?: boolean;
  } | null;
  /** #68 — lost & found: who may use the public forms and see the list, the desk, how long things are kept (`lostFoundSettingsOf`). */
  lostFound?: {
    reportLost?: string;
    reportFound?: string;
    showList?: string;
    deskServicePointId?: string | null;
    deskNote?: string | null;
    keepDays?: number;
  } | null;
  [key: string]: unknown;
}

/** ROADMAP #60 — an event's in-session upgrade offer: the percent off, when it closes, and the seconds left on the SERVER's clock. */
export interface EventUpgradeOfferInfo {
  percentOff: number;
  endsAt: string;
  secondsLeft: number;
}

export interface Event {
  id?: string;
  /** What the caller may do with this event: edit | remove | duplicate — `remove` only on an unpublished one. */
  rights?: string[] | null;
  accountId?: string | null;
  settings?: EventSettings | null;
  /** Selected website theme (soft ref → theme). */
  themeId?: string | null;
  /** Per-event token overrides merged over the theme's tokens (future: element overrides). */
  themeOverrides?: Record<string, unknown> | null;
  /** Per-event reusable custom CSS (future). */
  customCss?: string | null;
  title?: string | null;
  subtitle?: string | null;
  summary?: string | null;
  slug?: string | null;
  eventCategoryId?: string | null;
  eventNature?: string | null;
  audience?: string | null;
  hostedBy?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  /** ROADMAP A4: a RECURRING event's real end — its last live date's end (startsAt/endsAt are its FIRST date). Null otherwise. */
  seriesEndsAt?: string | null;
  /** ROADMAP A4: a RECURRING event's next date not over yet. Null when none, and on every other event. */
  nextDateStartsAt?: string | null;
  isAllDay?: boolean | null;
  timezone?: string | null;
  scheduleMode?: string | null;
  hasTimeSlots?: boolean | null;
  eventFormat?: string | null;
  venueId?: string | null;
  onlineMeetingUrl?: string | null;
  locationVisibility?: string | null;
  /** indoor | outdoor | mixed — physical setting; null = unspecified. */
  environment?: string | null;
  /** Event outcome depends on the weather (opts into future forecast-change alerts). */
  weatherSensitive?: boolean | null;
  organizerProfileId?: string | null;
  coverUploadedFileId?: string | null;
  coverUrl?: string | null;
  coverVideoUploadedFileId?: string | null;
  coverVideoUrl?: string | null;
  /** Resolved cover image file (federated from ms_storage). */
  coverUploadedFile?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  /** Resolved cover video file (federated from ms_storage). */
  coverVideoUploadedFile?: { id?: string | null; fileUrl?: string | null } | null;
  eventStatus?: string | null;
  /** When it first went public (the server stamps it once). #198 a — an event public before is never held for an email. */
  publishedAt?: string | null;
  isListed?: boolean | null;
  channel?: string | null;
  eventMode?: string | null;
  guestTracking?: string | null;
  /** yes_maybe_no | yes_no | submit — which RSVP buttons the guest sees. */
  rsvpButtonSet?: string | null;
  guestsNeedApproval?: boolean | null;
  /**
   * #37 phase 2 — on an approval-gated TICKETED event the buyer's card is authorized, not
   * charged. This much is kept per DENIED person (the non-refundable application fee);
   * null/0 releases the whole authorization.
   */
  approvalDepositAmount?: number | null;
  /** Age at which a guest signs their own waiver instead of a parent/guardian (default 18). */
  ageOfMajority?: number | null;
  /** #41 — minutes before the start that check-in opens; null = the 90-minute default. 0 = at the start. */
  doorsOpenMinutes?: number | null;
  capacity?: number | null;
  /** Per-EVENT override of the turnout warning (a share of capacity). Null uses the account default. */
  viableFillRate?: number | null;
  /** Per-EVENT override of the ticketed funding warning (a share of projected cost). Null uses the account default. */
  viableCostCoverage?: number | null;
  rsvpGroupSize?: number | null;
  /** #77 — ticketed: the most admission tickets one order may hold (null = no limit). */
  maxTicketsPerOrder?: number | null;
  rsvpOpensAt?: string | null;
  rsvpDeadline?: string | null;
  /** A2e #102 — a repeating event: each date closes this many days before it (0 = its own day); null = none. */
  rsvpDeadlineDaysBefore?: number | null;
  enableWaitlist?: boolean | null;
  currencyCode?: string | null;
  isTestMode?: boolean | null;
  serviceFeePayer?: string | null;
  feeMode?: string | null;
  taxSettingId?: string | null;
  minOrderAmount?: number | null;
  cancelReason?: string | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * Resolvable cover image URL for an event: the stored file's URL when set,
 * otherwise the external URL (Unsplash / Giphy). Null when there's no cover.
 */
export const eventCoverImageUrl = (event?: Event | null): string | null =>
  event?.coverUploadedFile?.fileUrl || event?.coverUrl || null;

/**
 * Resolvable cover video URL for an event: the stored file's URL when set,
 * otherwise the external URL (YouTube / Vimeo / direct). Null when there's none.
 */
export const eventCoverVideoUrl = (event?: Event | null): string | null =>
  event?.coverVideoUploadedFile?.fileUrl || event?.coverVideoUrl || null;

export interface EventWhereInput {
  id?: string | string[];
}

export interface EventFilter {
  id?: string[];
  eventStatus?: string[];
  eventMode?: string[];
  venueId?: string[];
  eventCategoryId?: string[];
  status?: number[];
  /** Free-text search over title + subtitle + summary (server-side). */
  searchKeyword?: string;
  /**
   * ROADMAP #112 — `upcoming` (still to come, on now, or not dated yet; soonest next first) | `past` (over; latest first).
   * A recurring event is over when its last live date is.
   */
  timeframe?: EventTimeframe;
}

export const EVENT_TIMEFRAME = { UPCOMING: "upcoming", PAST: "past" } as const;
export type EventTimeframe = (typeof EVENT_TIMEFRAME)[keyof typeof EVENT_TIMEFRAME];

export interface EventInput {
  title?: string | null;
  subtitle?: string | null;
  summary?: string | null;
  themeId?: string | null;
  themeOverrides?: Record<string, unknown> | null;
  customCss?: string | null;
  eventCategoryId?: string | null;
  audience?: string | null;
  hostedBy?: string | null;
  slug?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isAllDay?: boolean | null;
  timezone?: string | null;
  scheduleMode?: string | null;
  eventFormat?: string | null;
  venueId?: string | null;
  onlineMeetingUrl?: string | null;
  locationVisibility?: string | null;
  environment?: string | null;
  weatherSensitive?: boolean | null;
  organizerProfileId?: string | null;
  coverUploadedFileId?: string | null;
  coverUrl?: string | null;
  coverVideoUploadedFileId?: string | null;
  coverVideoUrl?: string | null;
  eventStatus?: string | null;
  isListed?: boolean | null;
  channel?: string | null;
  eventMode?: string | null;
  guestTracking?: string | null;
  rsvpButtonSet?: string | null;
  guestsNeedApproval?: boolean | null;
  /**
   * #37 phase 2 — on an approval-gated TICKETED event the buyer's card is authorized, not
   * charged. This much is kept per DENIED person (the non-refundable application fee);
   * null/0 releases the whole authorization.
   */
  approvalDepositAmount?: number | null;
  /** Age at which a guest signs their own waiver instead of a parent/guardian (default 18). */
  ageOfMajority?: number | null;
  /** #41 — minutes before the start that check-in opens; null = the 90-minute default. 0 = at the start. */
  doorsOpenMinutes?: number | null;
  capacity?: number | null;
  /** Per-EVENT override of the turnout warning (a share of capacity). Null uses the account default. */
  viableFillRate?: number | null;
  /** Per-EVENT override of the ticketed funding warning (a share of projected cost). Null uses the account default. */
  viableCostCoverage?: number | null;
  rsvpGroupSize?: number | null;
  /** #77 — ticketed: the most admission tickets one order may hold (null = no limit). */
  maxTicketsPerOrder?: number | null;
  rsvpOpensAt?: string | null;
  rsvpDeadline?: string | null;
  /** A2e #102 — a repeating event: each date closes this many days before it (0 = its own day); null = none. */
  rsvpDeadlineDaysBefore?: number | null;
  enableWaitlist?: boolean | null;
  currencyCode?: string | null;
  isTestMode?: boolean | null;
  serviceFeePayer?: string | null;
  feeMode?: string | null;
  taxSettingId?: string | null;
  minOrderAmount?: number | null;
  cancelReason?: string | null;
  settings?: EventSettings | null;
}

export class EventService {
  static async list(
    filter?: EventFilter,
    pagination?: Pagination,
    sortBy?: SortItem[],
  ): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /** One page of the account's events, with `total` (All Events). Upcoming / past and the search are the server's. */
  static async listPage(filter: EventFilter, pagination: Pagination): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_PAGE_QUERY, { filter, params: { pagination } });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_GET_QUERY, { id });
  }

  /**
   * ROADMAP #60 — the event's in-session upgrade offer while it is open, else null. ⚠ Never an error to show: any failure
   * (no offer field yet, a network blip) is "no offer" — the offer is a nudge, and its absence breaks nothing.
   */
  static async upgradeOffer(id: string): Promise<EventUpgradeOfferInfo | null> {
    const res: any = await ApiService.executeQuery(EVENT_UPGRADE_OFFER_QUERY, { id }, { fetchPolicy: "network-only" });
    const offer = res?.code === 0 && Array.isArray(res.data) ? res.data[0]?.upgradeOffer : null;

    return offer && Number(offer.percentOff) > 0 && Number(offer.secondsLeft) > 0
      ? { percentOff: Number(offer.percentOff), endsAt: String(offer.endsAt ?? ""), secondsLeft: Number(offer.secondsLeft) }
      : null;
  }

  static async create(params: EventInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_CREATE_MUTATION, { params });
  }

  static async update(where: EventWhereInput, params: EventInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_UPDATE_MUTATION, { where, params });
  }

  /** Permanently delete one (or several) events. Prefer `update({eventStatus:'canceled'})` for a soft cancel. */
  static async remove(where: EventWhereInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_REMOVE_MUTATION, { where });
  }

  /** Live availability check for a public-link slug (excludes the event being edited). */
  static async slugAvailable(slug: string, eventId?: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_SLUG_AVAILABLE_QUERY, { slug, eventId });
  }

  /**
   * How much of each PER-EVENT numeric ceiling this event has used, counted by the SAME
   * gateway methods the server's plan gate compares against.
   *
   * ⚠ Do not substitute a client-side `rows.length` for this. The list pages are paginated
   * and filtered to active rows; the ceiling counts every non-removed row whatever its
   * status. Counting locally shows "3 of 5" while the server counts 6 and refuses.
   */
  static async planUsage(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_PLAN_USAGE_QUERY, { params: { eventId } });
  }

  /**
   * A2b #56 — who cancelling this event would tell, what money is at stake, and (once it HAS been
   * cancelled) how the queued refunds are getting on.
   *
   * ⚠ A QUERY. The cancel dialog calls it on open, on a LIVE event — it must not be able to change
   * anything.
   */
  /** ROADMAP #116 — the copy drawer's checklist. ⚠ Read fresh: what the event holds changes under the host. */
  /**
   * ROADMAP #231 — what the event uses that its plan may not include: the SERVER's own publish / go-live check, so the
   * drawer lists exactly what a publish would refuse. `network-only`: an upgrade or a removal changes it.
   */
  static async planCheck(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_PLAN_CHECK_QUERY, { params: { eventId } }, { fetchPolicy: "network-only" });
  }

  static async copyPlan(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_COPY_PLAN_QUERY, { params: { eventId } }, { fetchPolicy: "network-only" });
  }

  /** ROADMAP #116 — copy an event: a new draft with the ticked parts (`groups`). Answers the new event and what was left out. */
  static async copy(params: { eventId: string; title?: string | null; startsAt: string; groups: string[] }): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_COPY_MUTATION, { params });
  }

  static async cancelStatus(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_CANCEL_STATUS_QUERY, { params: { eventId } });
  }

  /** ROADMAP A2b #63 — see EVENT_FEATURE_USAGE_QUERY. A QUERY: one SQL read plus one file-storage probe. */
  static async featureUsage(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_FEATURE_USAGE_QUERY, { params: { eventId } });
  }

  /** ROADMAP #161 b — see EVENT_PAID_SALES_QUERY. */
  static async paidSales(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_PAID_SALES_QUERY, { params: { eventId } });
  }

  /** ROADMAP #188 — see EVENT_TEST_RECORDS_QUERY. ⚠ Read fresh: the host decides on it (Clear), and a sale may land. */
  static async testRecords(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_TEST_RECORDS_QUERY, { params: { eventId } }, { fetchPolicy: "network-only" });
  }

  /** ROADMAP #188 — see EVENT_TEST_RECORDS_CLEAR_MUTATION. */
  static async clearTestRecords(eventId: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_TEST_RECORDS_CLEAR_MUTATION, { params: { eventId } });
  }

  /** ROADMAP #190 — see EVENT_DASHBOARD_CARDS_QUERY. Read fresh: a save on another screen must show. */
  static async dashboardCards(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_DASHBOARD_CARDS_QUERY, { params: { eventId } }, { fetchPolicy: "network-only" });
  }

  /** ROADMAP #190 — see EVENT_DASHBOARD_CARDS_SAVE_MUTATION. `cards: null` → the account's default. */
  static async saveDashboardCards(eventId: string, cards: EventDashboardCard[] | null): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_DASHBOARD_CARDS_SAVE_MUTATION, { params: { eventId, cards } });
  }

  /** #62 P6(d4) — see EVENT_PREVIEW_LINK_MUTATION. A MUTATION: it mints a credential. */
  static async previewLink(eventId: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_PREVIEW_LINK_MUTATION, { params: { eventId } });
  }

  /**
   * A2b #56 — cancel the event: close it down, tell everyone involved, and (only when the host
   * explicitly asked) QUEUE every outstanding payment to go back.
   *
   * ⚠⚠ DO NOT CANCEL WITH `update({ eventStatus: 'canceled' })` — the server REFUSES it. That was
   * how the product used to do it, and it told nobody: the dialog promised "your guests are
   * notified" and sent nothing.
   * ⚠ `refundEveryone` QUEUES refunds; a cancelled festival is thousands of Stripe calls, so they
   * are settled by a background sweep. Re-read `cancelStatus` for progress.
   */
  static async cancel(params: { eventId: string; reason?: string | null; refundEveryone?: boolean }): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_CANCEL_MUTATION, { params });
  }

  /**
   * A2b #56 — put this event's FAILED cancellation refunds back in the queue.
   *
   * ⚠ The escape hatch for an attempt cap that guessed wrong. Without it a failed row is a dead end
   * and the money is never returned, whatever the host does.
   */
  static async retryCancelRefunds(eventId: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_CANCEL_RETRY_MUTATION, { params: { eventId } });
  }
}
